import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import heroDemo from 'assets/images/recovery-ai/img_hero_demo.gif'
import ArrowRight from 'components/Icons/ArrowRight'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import { usePermissionReason } from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import React from 'react'
import { useDispatch } from 'react-redux'
import { push } from 'react-router-redux'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { useAbility } from 'utilities/ability'
import SectionShell from './SectionShell'
import usePromoCtaTarget from './usePromoCtaTarget'

const t = buildTranslate('recovery_ai.promo.hero')
const tCta = buildTranslate('recovery_ai.promo.cta')
const tPermission = buildTranslate('nav.permission_tooltip')

// Figma 129:1741. Section padding, the two-column gap, and the phone mockup box
// are all fixed in the design, so they live here as named constants.
const SECTION_PADDING = '48px 86px 24px'
const CONTENT_GAP = '24px'
const COPY_GAP = '12px'
const CTA_GAP = '16px'
const TITLE_LINE_HEIGHT = '56px'
const SUBTITLE_LINE_HEIGHT = '24px'
// The Figma button pairs its label with a 20px trailing arrow (matches CtaSection).
const ARROW_SIZE = 20

// The exported "recovery-ai-demo 3" asset (760x570) is a wide capture of the
// demo; Figma crops it to the phone by over-sizing the image inside a fixed
// 331x570 box and shifting it left. These percentages reproduce that crop.
const PHONE_BOX_WIDTH = '331px'
const PHONE_BOX_HEIGHT = '570px'
const PHONE_IMAGE_WIDTH = '229.61%'
const PHONE_IMAGE_LEFT = '-64.05%'

const Hero = () => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  // A merchant who already finished onboarding goes to the activation CTA, not
  // back through the wizard (shared with CtaSection — see usePromoCtaTarget).
  const ctaTarget = usePromoCtaTarget()
  // An RBAC view-only operator can reach the promo page but must not be able to
  // start activation. Same 'manage' gate the Settings panes apply (AI-64), and
  // the same PermissionTooltip/reason plumbing every other write-gated control
  // uses — the reason resolves only when the RBAC tooltip flags are on.
  const ability = useAbility()
  const cannotManage = ability.cannot('manage', 'MerchantSettings')
  const activateDisabledReason = usePermissionReason(
    'MerchantSettings',
    cannotManage,
    tPermission('view_and_edit_suffix')
  )

  return (
    <SectionShell
      backgroundColor={merchantTheme.colors.grey05}
      padding={SECTION_PADDING}
    >
      <div
        className={css({
          alignItems: 'center',
          display: 'flex',
        })}
      >
        <div
          className={css({
            display: 'flex',
            flex: '1 0 0',
            flexDirection: 'column',
            gap: CONTENT_GAP,
            minWidth: 0,
          })}
        >
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: COPY_GAP,
            })}
          >
            <Text.Header1
              tag="h1"
              color="grey90"
              className={css({ lineHeight: TITLE_LINE_HEIGHT, margin: 0 })}
            >
              {t('title')}
            </Text.Header1>
            <Text.BodyText2
              tag="p"
              color="grey70"
              className={css({ lineHeight: SUBTITLE_LINE_HEIGHT, margin: 0 })}
            >
              {t('subtitle')}
            </Text.BodyText2>
          </div>

          {/* DELIBERATE DEVIATION from Figma 129:1741, which shows two CTAs
            ("Pilot at select locations" / "Turn on for all N locations"). Both
            would need a real location count and two distinct activation
            flows; until those exist this hero ships the same single primary
            CTA as the closing CtaSection band. */}
          <div
            className={css({
              alignItems: 'flex-start',
              display: 'flex',
              flexDirection: 'column',
              gap: CTA_GAP,
            })}
          >
            <PermissionTooltip
              disabled={cannotManage}
              reason={activateDisabledReason}
              id="recovery-ai-promo-hero-activate-perm"
              placement="top"
              inline
            >
              <Button
                kind="primary"
                disabled={cannotManage}
                onClick={() => dispatch(push(ctaTarget))}
                endEnhancer={
                  <ArrowRight
                    className={css({
                      height: `${ARROW_SIZE}px`,
                      width: `${ARROW_SIZE}px`,
                    })}
                    fillColor={merchantTheme.colors.white}
                  />
                }
              >
                {tCta('activate')}
              </Button>
            </PermissionTooltip>
          </div>
        </div>

        <div
          className={css({
            flexShrink: 0,
            height: PHONE_BOX_HEIGHT,
            overflow: 'hidden',
            position: 'relative',
            width: PHONE_BOX_WIDTH,
          })}
        >
          <img
            alt={t('phone_alt')}
            src={heroDemo}
            className={css({
              height: '100%',
              left: PHONE_IMAGE_LEFT,
              maxWidth: 'none',
              position: 'absolute',
              top: 0,
              width: PHONE_IMAGE_WIDTH,
            })}
          />
        </div>
      </div>
    </SectionShell>
  )
}

export default Hero
